import {
  Box,
  Typography,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import type { KeyboardEvent } from "react";
import type {
  MaterialSupplierForm,
  Formular,
} from "@/api/materialSupplierService";
import FormularField from "./FormularField";

interface PriceCalculationFieldsProps {
  formData: MaterialSupplierForm;
  setFormData: (data: MaterialSupplierForm) => void;
  formular: Formular | null;
  formularData?: Formular[];
  isLoadingFormulars: boolean;
  errors: Record<string, string>;
  materialSupplierId?: number;
  setErrors: (errors: Record<string, string>) => void;
}

const PriceCalculationFields = ({
  formData,
  setFormData,
  formular,
  formularData,
  isLoadingFormulars,
  materialSupplierId,
  errors,
  setErrors,
}: PriceCalculationFieldsProps) => {
  return (
    <>
      <Typography variant="h3" mt={2}>
        คำนวณ Material Price
      </Typography>
      <Divider />
      <Box sx={{ mt: 2, width: "30%" }}>
        <FormControl
          disabled={!!materialSupplierId}
          fullWidth
          error={!!errors.formula_id}
        >
          <InputLabel
            id="formular-label"
            sx={{
              backgroundColor: "#fff",
              px: 0.5,
              "&.Mui-focused": {
                color: "primary.main",
              },
            }}
          >
            Formular{" "}
            <Typography component="span" color="error.main">
              *
            </Typography>
          </InputLabel>
          <Select
            labelId="formular-label"
            value={formData.formula_id || ""}
            onChange={(e) => {
              setFormData({
                ...formData,
                formula_id: Number(e.target.value),
              });
              if (errors.formula_id) {
                setErrors({ ...errors, formula_id: "" });
              }
            }}
            error={!!errors.formula_id}
          >
            {isLoadingFormulars ? (
              <MenuItem disabled>กำลังโหลด...</MenuItem>
            ) : formularData && formularData.length > 0 ? (
              formularData.map((option) => (
                <MenuItem key={option.formula_id} value={option.formula_id}>
                  <Typography>{option.formula_name}</Typography>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>ไม่พบข้อมูล</MenuItem>
            )}
          </Select>
          {errors.formula_id && (
            <FormHelperText>{errors.formula_id}</FormHelperText>
          )}
        </FormControl>
      </Box>
      <Box display="flex" gap={2}>
        {formular?.has_commission_pct && (
          <TextField
            label={
              <>
                Commission{" "}
                <Typography component="span" color="error.main">
                  *
                </Typography>
              </>
            }
            variant="outlined"
            type="number"
            value={String(formData.attributes.commission_pct)}
            onChange={(e) => {
              const value = Number(e.target.value);
              setFormData({
                ...formData,
                attributes: {
                  ...formData.attributes,
                  commission_pct: value,
                },
              });
              if (errors.commission_pct) {
                setErrors({ ...errors, commission_pct: "" });
              }
            }}
            sx={{ width: "12%" }}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
            error={!!errors.commission_pct}
            helperText={errors.commission_pct}
          />
        )}
        {formular?.has_stock_pct && (
          <TextField
            label={
              <>
                Stock{" "}
                <Typography component="span" color="error.main">
                  *
                </Typography>
              </>
            }
            value={String(formData.attributes.stock_pct)}
            onChange={(e) => {
              setFormData({
                ...formData,
                attributes: {
                  ...formData.attributes,
                  stock_pct: Number(e.target.value),
                },
              });
              if (errors.stock_pct) {
                setErrors({ ...errors, stock_pct: "" });
              }
            }}
            variant="outlined"
            type="number"
            sx={{ width: "12%" }}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
            error={!!errors.stock_pct}
            helperText={errors.stock_pct}
          />
        )}
        {formular?.has_credit_pct && (
          <TextField
            label={
              <>
                Credit{" "}
                <Typography component="span" color="error.main">
                  *
                </Typography>
              </>
            }
            variant="outlined"
            type="number"
            value={String(formData.attributes.credit_pct)}
            onChange={(e) => {
              setFormData({
                ...formData,
                attributes: {
                  ...formData.attributes,
                  credit_pct: Number(e.target.value),
                },
              });
              if (errors.credit_pct) {
                setErrors({ ...errors, credit_pct: "" });
              }
            }}
            sx={{ width: "12%" }}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
            error={!!errors.credit_pct}
            helperText={errors.credit_pct}
          />
        )}
        {formular?.has_rounding && formular?.has_rounding_digits && (
          <Box sx={{ width: "20%" }} display="flex" gap={1}>
            <FormControl sx={{ width: "65%" }} error={!!errors.rounding}>
              <InputLabel
                id="round-label"
                sx={{
                  backgroundColor: "#fff",
                  px: 0.5,
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                }}
                error={!!errors.rounding}
              >
                Rounding{" "}
                <Typography component="span" color="error.main">
                  *
                </Typography>
              </InputLabel>
              <Select
                labelId="round-label"
                value={formData.attributes.rounding || "ROUND"}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    attributes: {
                      ...formData.attributes,
                      rounding: e.target.value,
                    },
                  });
                  setErrors({ ...errors, rounding: "" });
                }}
                error={!!errors.rounding}
              >
                <MenuItem value="ROUND">
                  <Typography>Round</Typography>
                </MenuItem>
                <MenuItem value="ROUND UP">
                  <Typography>Round Up</Typography>
                </MenuItem>
                <MenuItem value="ROUND DOWN">
                  <Typography>Round Down</Typography>
                </MenuItem>
              </Select>
              {errors.rounding && (
                <FormHelperText>{errors.rounding}</FormHelperText>
              )}
            </FormControl>
            <TextField
              label={
                <>
                  Digits{" "}
                  <Typography component="span" color="error.main">
                    *
                  </Typography>
                </>
              }
              value={String(formData.attributes.rounding_digits)}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  attributes: {
                    ...formData.attributes,
                    rounding_digits: Number(e.target.value),
                  },
                });
                if (errors.rounding_digits) {
                  setErrors({ ...errors, rounding_digits: "" });
                }
              }}
              variant="outlined"
              type="number"
              sx={{ width: "35%" }}
              slotProps={{
                htmlInput: {
                  min: 0,
                  step: 1,
                },
                input: {
                  onKeyPress: (e: KeyboardEvent<HTMLInputElement>) => {
                    if (
                      e.key === "." ||
                      e.key === "e" ||
                      e.key === "E" ||
                      e.key === "-" ||
                      e.key === "+"
                    ) {
                      e.preventDefault();
                    }
                  },
                },
              }}
              error={!!errors.rounding_digits}
              helperText={errors.rounding_digits}
            />
          </Box>
        )}
        {formular?.has_usance && (
          <TextField
            label={
              <>
                Usance{" "}
                <Typography component="span" color="error.main">
                  *
                </Typography>
              </>
            }
            variant="outlined"
            type="number"
            value={String(formData.attributes.usance)}
            onChange={(e) => {
              setFormData({
                ...formData,
                attributes: {
                  ...formData.attributes,
                  usance: Number(e.target.value),
                },
              });
              if (errors.usance) {
                setErrors({ ...errors, usance: "" });
              }
            }}
            sx={{ width: "12%" }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">Days</InputAdornment>
                ),
              },
            }}
            error={!!errors.usance}
            helperText={errors.usance}
          />
        )}
        {formular?.has_interest && (
          <TextField
            label={
              <>
                Interest{" "}
                <Typography component="span" color="error.main">
                  *
                </Typography>
              </>
            }
            variant="outlined"
            type="number"
            sx={{ width: "12%" }}
            value={String(formData.attributes.interest)}
            onChange={(e) => {
              setFormData({
                ...formData,
                attributes: {
                  ...formData.attributes,
                  interest: Number(e.target.value),
                },
              });
              if (errors.interest) {
                setErrors({ ...errors, interest: "" });
              }
            }}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
            error={!!errors.interest}
            helperText={errors.interest}
          />
        )}
      </Box>
      <FormularField formular={formular} />
    </>
  );
};

export default PriceCalculationFields;
